// ─── Cierre de sesión por inactividad ────────────────────────────────────────
// Política configurable por un administrador en Usuarios (erp:politica_sesion:
// { habilitado, minutos }). Tras X minutos sin actividad —tecla, clic, rueda,
// toque o movimiento del ratón— se cierra la sesión. Un minuto antes aparece
// una alerta pequeña con cuenta regresiva: cualquier actividad la disuelve y
// reinicia el reloj, así que a nadie se le cierra la sesión mientras trabaja.
//
// La política se relee EN CADA REVISIÓN, no al arrancar: si el administrador
// la cambia, tenant_config la propaga por realtime al caché local y las
// sesiones abiertas obedecen el valor nuevo sin recargar.
//
// El límite se calcula con hora de pared (Date.now), no con tics: si el
// equipo durmió más allá del plazo, al despertar la sesión se cierra de
// inmediato — dejarla viva "porque el temporizador no corrió" sería
// exactamente el agujero que esta política tapa.
import { anotarSalida } from "../core/auditLog.js";
import { PRIORIDAD, anclarConEntrada } from "./dock.js";
import { vaciarSincronizacionPendiente } from "../core/cloudSync.js";
import { C } from "../core/config.js";
import { supa } from "../core/supabaseClient.js";

const AVISO_MS = 60 * 1000;          // la cuenta regresiva del último minuto
const MARCA_CIERRE = "aura:cierre_inactividad";

export function getPoliticaSesion() {
  try {
    const raw = localStorage.getItem("erp:politica_sesion");
    const p = raw ? JSON.parse(raw) : {};
    return { habilitado: !!p.habilitado, minutos: Math.min(480, Math.max(5, Number(p.minutos) || 15)) };
  } catch { return { habilitado: false, minutos: 15 }; }
}

// Mensaje para la pantalla de login cuando el cierre fue por inactividad.
// Se consume una sola vez (sessionStorage sobrevive a la recarga de la pestaña).
export function motivoCierreInactividad() {
  try {
    if (sessionStorage.getItem(MARCA_CIERRE)) {
      sessionStorage.removeItem(MARCA_CIERRE);
      return "Por tu seguridad, la sesión se cerró tras un periodo de inactividad. Todo tu trabajo quedó guardado. Vuelve a iniciar sesión.";
    }
  } catch { /* sin sessionStorage no hay marca */ }
  return null;
}

let ultimaActividad = Date.now();
let vigilando = false;

export function iniciarVigilanciaInactividad() {
  if (vigilando) return;
  vigilando = true;
  ultimaActividad = Date.now();

  const marcar = () => {
    ultimaActividad = Date.now();
    const aviso = document.getElementById("aviso-inactividad");
    if (aviso) aviso.remove();
  };
  ["pointerdown", "keydown", "wheel", "touchstart"].forEach(ev =>
    document.addEventListener(ev, marcar, { capture: true, passive: true }));
  // El movimiento del ratón se atenúa a una marca por segundo: registrar cada
  // píxel sería trabajo inútil en el hilo principal.
  let ultimoMove = 0;
  document.addEventListener("mousemove", () => {
    const t = Date.now();
    if (t - ultimoMove > 1000) { ultimoMove = t; marcar(); }
  }, { capture: true, passive: true });

  setInterval(revisar, 5000);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) revisar(); });
}

function revisar() {
  const p = getPoliticaSesion();
  if (!p.habilitado) {
    const aviso = document.getElementById("aviso-inactividad");
    if (aviso) aviso.remove();
    return;
  }
  const limite = ultimaActividad + p.minutos * 60000;
  const ahora = Date.now();
  if (ahora >= limite) { cerrarPorInactividad(); return; }
  if (limite - ahora <= AVISO_MS) mostrarAvisoInactividad(limite);
}

async function cerrarPorInactividad() {
  // Guardas dobles: si la tarjeta ya inició el cierre, no repetirlo.
  if (window.__cerrandoPorInactividad) return;
  window.__cerrandoPorInactividad = true;
  try { sessionStorage.setItem(MARCA_CIERRE, "1"); } catch { /* la recarga mostrará el login igual */ }
  try {
    await Promise.race([vaciarSincronizacionPendiente(), new Promise(r => setTimeout(r, 6000))]);
  } catch { /* se cierra igual */ }
  await anotarSalida("salida_inactividad");
  try { await supa.auth.signOut(); } catch { /* la recarga limpia el resto */ }
  location.reload();
}

function mostrarAvisoInactividad(limite) {
  if (document.getElementById("aviso-inactividad")) return;

  const tarjeta = document.createElement("div");
  tarjeta.id = "aviso-inactividad";
  tarjeta.style.cssText = `
    width:330px;max-width:calc(100vw - 32px);
    background:white;border:1px solid #fde68a;border-left:4px solid ${C.amber};
    border-radius:10px;box-shadow:0 12px 34px rgba(15,23,42,.22);padding:13px 14px;
    font-family:inherit;
  `;
  tarjeta.innerHTML = `
    <div style="display:flex;gap:9px;align-items:flex-start;">
      <div style="font-size:20px;line-height:1;">⏱️</div>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;font-weight:800;color:${C.text};">¿Sigues ahí?</div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:3px;line-height:1.45;">
          Por seguridad, la sesión se cerrará en
          <b id="aviso-inactividad-reloj" style="color:#b45309;font-variant-numeric:tabular-nums;">1:00</b>
          por inactividad. Tu trabajo ya está guardado.
        </div>
        <div style="display:flex;gap:6px;margin-top:9px;">
          <button id="aviso-inactividad-seguir" style="
            background:${C.mathiz};color:white;border:none;border-radius:6px;
            padding:6px 12px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;
          ">Seguir trabajando</button>
        </div>
      </div>
    </div>
  `;
  anclarConEntrada(tarjeta, PRIORIDAD.inactividad);

  const reloj = tarjeta.querySelector("#aviso-inactividad-reloj");
  const tic = setInterval(() => {
    // Cualquier actividad retiró la tarjeta (ver marcar()): el reloj muere con ella.
    if (!document.getElementById("aviso-inactividad")) { clearInterval(tic); return; }
    const resta = Math.max(0, limite - Date.now());
    const m = Math.floor(resta / 60000);
    const s = Math.floor((resta % 60000) / 1000);
    if (reloj) reloj.textContent = `${m}:${String(s).padStart(2, "0")}`;
    if (resta <= 0) { clearInterval(tic); cerrarPorInactividad(); }
  }, 250);

  // El clic del botón ya cuenta como actividad (pointerdown la marca), pero se
  // retira explícitamente por si el clic llegó por teclado (Enter).
  tarjeta.querySelector("#aviso-inactividad-seguir").onclick = () => {
    clearInterval(tic);
    tarjeta.remove();
  };
}
